import {inject, observer} from "mobx-react";
import React, {useEffect, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPencilAlt, faSpinner, faWindowClose} from "@fortawesome/free-solid-svg-icons";
import {fetch} from "../../utils/api-wrapper";
import AdminTable from "../../components/admin-table";
import Modal from "../../components/modal";
import RolesEditForm from "../../components/roles-edit-form";
import RoleSettingsModal from "../../components/RoleSettingsModal";
import toast from "react-hot-toast";

const ROLES_FILTERS = ["expenses"];

const PRIMARY_ROLES = {
    superadmin: "SuperAdmin",
    admin: "Admin",
    manager: "Manager",
    clerk: "Clerk",
    approver: "Approver",
    assistant: "Assistant"
};

const DEFAULT_ROLE_EXCLUDED_NAMES = new Set(["SuperAdmin", "Admin", "Manager"]);

const AdminRoles = ({data}) => {
    const [errorMessage, setErrorMessage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [components, setComponents] = useState([]);
    const [actionsList, setActionsList] = useState([]);
    const [actionsByComponents, setActionsByComponents] = useState({});

    const [roles, setRoles] = useState([]);

    const [showRoleEditForm, setShowRoleEditForm] = useState(false);
    const [updatedRole, setUpdatedRole] = useState({});
    const [rolesDeleteSelection, setRolesDeleteSelection] = useState([]);
    const [showDeleteRoleModal, setShowDeleteRoleModal] = useState(false);
    const [showRoleSettings, setShowRoleSettings] = useState(false);
    const [selectedDefaultRoleId, setSelectedDefaultRoleId] = useState(null);
    const [savingDefaultRole, setSavingDefaultRole] = useState(false);
    const [defaultRoleError, setDefaultRoleError] = useState(null);

    useEffect(() => {
        setLoading(true);
        refreshRoles();
        fetchComponents();
        fetchActions();
    }, []);

    const roleIdOf = (role) => role?._id?.$oid || role?._id;

    const openRoleEditor = (role) => {
        setUpdatedRole(role || {});
        setShowRoleEditForm(true);
        setErrorMessage(null);
    };

    const eligibleDefaultRoles = roles.filter(
        (role) => !DEFAULT_ROLE_EXCLUDED_NAMES.has(role.name)
    );

    const openRoleSettings = () => {
        // Clerk is the backend fallback for legacy tenants that do not yet have
        // an explicit is_default flag, so show the same effective selection.
        const currentDefault = eligibleDefaultRoles.find((role) => role.is_default)
            || eligibleDefaultRoles.find((role) => role.name === PRIMARY_ROLES.clerk);
        setSelectedDefaultRoleId(currentDefault ? roleIdOf(currentDefault) : null);
        setDefaultRoleError(null);
        setShowRoleSettings(true);
    };

    const saveDefaultRole = () => {
        if (!selectedDefaultRoleId || savingDefaultRole) return;
        setSavingDefaultRole(true);
        setDefaultRoleError(null);
        fetch(
            "/roles/default",
            "PUT",
            {role_id: selectedDefaultRoleId},
            () => {
                setSavingDefaultRole(false);
                setShowRoleSettings(false);
                refreshRoles();
                toast.success(data.t.read("roles.default_role_save_success"));
            },
            () => {
                setSavingDefaultRole(false);
                setDefaultRoleError(data.t.read("roles.default_role_save_error"));
            }
        );
    };

    const refreshRoles = () =>
        fetch(
            `/roles/?filters=%5B%5D&sorting=%5B%5D&start=0&page_size=50`,
            "GET",
            {},
            (response) => {
                const list = response.data.roles || [];
                setRoles(list);
                setLoading(false);
            },
            () => {
                setLoading(false);
                setErrorMessage(data?.t?.read("roles.load_error") || "Failed to load roles.");
            }
        );

    const fetchActions = () => {
        fetch(
            "/roles/actions/",
            "GET",
            {},
            (response) => {
                setActionsList(response.data.actions_list || []);
                setActionsByComponents(response.data.actions_by_component || {});
            },
            () => {}
        );
    };

    const fetchComponents = () =>
        fetch("/roles/modules", "GET", {}, (response) => {
            const filtered_components = (response.data || []).filter((c) => ROLES_FILTERS.includes(c.name));

            setComponents(filtered_components || []);
        });
    
    const updateRole = (role = {...updatedRole}) => {
        const roleWithoutId = { ...role};
        delete roleWithoutId._id;
        const roleId = roleIdOf(role);
        setSaving(true);
        fetch(`/roles/${roleId}`, "PUT", roleWithoutId, () => {
            refreshRoles();
            toast.success(data?.t?.read("roles.save_success") || "Saved.");
            setSaving(false);
        },
            () => {
                setSaving(false);
                setErrorMessage(data?.t?.read("roles.save_error") || "Failed to save roles.");
            })
    }
    
    const deleteRoles = () => {
        fetch("/roles/", "DELETE", {role_ids: rolesDeleteSelection}, () => {
            refreshRoles();
            setRolesDeleteSelection([]);
            setShowDeleteRoleModal(false);
            toast.success(data?.t?.read("roles.delete_success") || "Role deleted.");
        }, () => {
            setErrorMessage(data?.t?.read("roles.delete_failed") || "Failed to delete role.");
        })
    }
    const createRole = () => {
        const roleWithoutId = {...updatedRole};
        delete roleWithoutId._id;
        setSaving(true);
        fetch(`/roles/`, "POST", roleWithoutId, () => {
            refreshRoles();
            toast.success(data?.t?.read("roles.save_success") || "Saved.");
            setSaving(false);
        },
        () => {
            setSaving(false);
            setErrorMessage(data?.t?.read("roles.save_error") || "Failed to save roles.");
        })
    }

    const handleSubmit = (e) =>{
        e.preventDefault();
        if (saving) return;
        setShowRoleEditForm(false);
        if(updatedRole._id){
            updateRole();
        }
        else{
            createRole();
        }

    }

    const tableColumns = [
        {
            id: "name", 
            className: "pointer", 
            label: data.t.read("name"), 
            onClick: (role) => openRoleEditor(role), 
            render: (role)=>{
                const isPrimaryRole = Object.values(PRIMARY_ROLES).includes(role.name);
                const primaryRoleTranslationKey = role.name?.toLowerCase();
                return isPrimaryRole && primaryRoleTranslationKey
                    ? data?.t?.read(`roles.${primaryRoleTranslationKey}`) || role.name
                    : role.name
                
            }
        }, 
        {
            id: "description", 
            className: "pointer", 
            label: data.t.read("description"),
            onClick: (role) => openRoleEditor(role),
            render: (role) => role.description
        }, 
        {
            id: "actions", 
            className: "text-end actions-col", 
            label: "Actions", 
            onClick: () => {},
            render: (role) => {
                const isPrimaryRole = Object.values(PRIMARY_ROLES).includes(role.name);

                return( <div className={"flex "}>
                    <FontAwesomeIcon
                        className="mr-2" icon={faPencilAlt}
                        onClick={() => openRoleEditor(role)}
                        type="button"
                    />
                    {
                        !isPrimaryRole ? <FontAwesomeIcon
                            onClick={() => {
                                setRolesDeleteSelection([roleIdOf(role)]);
                                setShowDeleteRoleModal(true)
                            }}
                            icon={faWindowClose} type="button"
                            style={{color: 'red'}}/> : null
                    }
                    
                </div>)
            }
    },]

    return (!loading ? (<div style={{height: '80vh'}}>
        <div>
            {/* Action Buttons*/}
            <div className="d-flex justify-content-end align-items-center py-2">
                <button
                    onClick={openRoleSettings}
                    type="button"
                    className="btn btn-outline-secondary btn-sm ml-2">
                    {data.t.read("settings")}
                </button>
                <button
                    onClick={() => openRoleEditor({})}
                    type="button"
                    className='btn btn-primary btn-sm ml-2'>
                    {data.t.read("add")}
                </button>
            </div>
            <AdminTable headers={tableColumns} data={roles}/>

        </div>
        {errorMessage != null && (
            <Modal title={data.t.read("attention")} okText={"OK"} okAction={() => setErrorMessage(null)}>
                <p style={{whiteSpace: 'pre-line'}} className='text-danger'>{errorMessage}</p>
            </Modal>
        )}
        {
            showRoleEditForm && (
                <RolesEditForm
                    data={data}
                    handleSubmit={handleSubmit}
                    updatedRole={updatedRole}
                    setUpdatedRole={setUpdatedRole}
                    cancelForm={() => setShowRoleEditForm(false)}
                    components={components}
                    actionsList={actionsList}
                    actionsByComponents={actionsByComponents}
                />
            )
        }
        {showDeleteRoleModal && (<Modal
            title={data.t.read("delete") || "Delete"}
            okAction={deleteRoles}
            cancelAction={()=>{setShowDeleteRoleModal(false);  setRolesDeleteSelection([])}}
            okText={data.t.read("delete") || "Delete"}
            cancelText={data.t.read("cancel") || "Cancel"}>
            {data?.t?.read("roles.delete_confirmation_message") || "Are you sure you want to delete this role?"}
            </Modal>)}
        {showRoleSettings && (
            <RoleSettingsModal
                data={data}
                roles={eligibleDefaultRoles}
                selectedRoleId={selectedDefaultRoleId}
                setSelectedRoleId={setSelectedDefaultRoleId}
                save={saveDefaultRole}
                cancel={() => setShowRoleSettings(false)}
                saving={savingDefaultRole}
                error={defaultRoleError}
            />
        )}
        
        

    </div>) : (<div className="d-flex loading-container">
        <FontAwesomeIcon className="infinite-rotate" icon={faSpinner}/>
    </div>))
}

export default inject("data")(observer(AdminRoles));
